import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API, formatApiError, getAccessToken } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, Download, CheckCircle2, XCircle, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 25a — new-import flow.
 * Route: /app/admin/imports/new       — type picker + upload
 * Route: /app/admin/imports/:jobId    — dry-run preview + commit + progress
 */
export default function ImportNewPage() {
  const { jobId } = useParams();
  return jobId ? <JobDetail jobId={jobId} /> : <TypePickerAndUpload />;
}

// -------------------------------------------------------------------------
// Type picker + upload
// -------------------------------------------------------------------------
function TypePickerAndUpload() {
  const [types, setTypes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/tenant/imports/types").then(({ data }) => setTypes(data.items || []));
  }, []);

  const download = (t) => {
    api.get(`/tenant/imports/templates/${t}.csv`, { responseType: "blob" })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url; a.download = `template-${t}.csv`;
        a.click(); URL.revokeObjectURL(url);
      });
  };

  const upload = async (file) => {
    if (!selected) return toast.error("Pick an import type first");
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/tenant/imports/${selected}/dry-run`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      navigate(`/app/admin/imports/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="import-new-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
          <Link to="/app/admin/imports" className="hover:underline">Imports</Link> / New
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">New bulk import</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Pick a record type, download the template CSV, fill it in, then
          upload. Every upload runs a dry-run first — nothing writes to
          production until you review the preview and confirm the commit.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle>1. Pick a record type</CardTitle></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {types.map((t) => (
            <button
              key={t.type}
              type="button"
              onClick={() => setSelected(t.type)}
              data-testid={`import-type-${t.type}`}
              className={
                "rounded-lg border p-3 text-left transition-colors " +
                (selected === t.type
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border hover:border-primary/40")
              }
            >
              <div className="flex items-center gap-2 font-medium">
                <FileText className="h-4 w-4 text-secondary" />
                {t.label}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{t.description}</p>
              <span
                onClick={(e) => { e.stopPropagation(); download(t.type); }}
                className="mt-2 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-secondary hover:underline"
                data-testid={`import-template-${t.type}`}
              >
                <Download className="h-3 w-3" /> Template
              </span>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>2. Upload a CSV</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border py-10 text-center"
            data-testid="import-upload-area"
          >
            <Upload className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              {selected
                ? `Uploading as: ${types.find((t) => t.type === selected)?.label || selected}`
                : "Pick a type above, then upload."}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid="import-upload-input"
            />
            <Button
              type="button"
              disabled={!selected || busy}
              onClick={() => fileInputRef.current?.click()}
              data-testid="import-upload-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Choose CSV
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// -------------------------------------------------------------------------
// Job detail — preview + commit + SSE progress
// -------------------------------------------------------------------------
function JobDetail({ jobId }) {
  const [job, setJob] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [progress, setProgress] = useState(null);
  const eventSourceRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/tenant/imports/${jobId}`);
      setJob(data);
      return data;
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [jobId]);

  const isTerminal = job && [
    "committed", "committed_with_errors", "failed", "failed_timeout",
  ].includes(job.status);

  const commit = async () => {
    if (!job) return;
    setCommitting(true);
    try {
      await api.post(`/tenant/imports/${jobId}/commit`);
      // Start SSE
      const token = getAccessToken();
      const url = `${API}/tenant/imports/${jobId}/progress?token=${encodeURIComponent(token || "")}`;
      const es = new EventSource(url);
      eventSourceRef.current = es;
      es.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          setProgress(data);
          if (["committed", "committed_with_errors", "failed", "failed_timeout"].includes(data.status)) {
            es.close();
            load();
            setCommitting(false);
          }
        } catch (_e) { /* ignore */ }
      };
      es.addEventListener("done", () => { es.close(); load(); setCommitting(false); });
      es.onerror = () => {
        es.close();
        // Fall back to polling.
        pollUntilDone();
      };
    } catch (e) {
      setCommitting(false);
      toast.error(formatApiError(e));
    }
  };

  const pollUntilDone = async () => {
    for (let i = 0; i < 240; i++) {
      const j = await load();
      if (!j) break;
      if (["committed", "committed_with_errors", "failed", "failed_timeout"].includes(j.status)) {
        setCommitting(false);
        return;
      }
      await new Promise((r) => setTimeout(r, 500));
    }
    setCommitting(false);
  };

  useEffect(() => () => { eventSourceRef.current?.close(); }, []);

  const downloadErrors = () => {
    api.get(`/tenant/imports/${jobId}/errors.csv`, { responseType: "blob" })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url; a.download = `import-${job.type}-${jobId}-errors.csv`;
        a.click(); URL.revokeObjectURL(url);
      });
  };

  if (!job) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="import-detail-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const canCommit = job.status === "dry_run_ready" && job.valid_rows > 0;

  return (
    <div className="space-y-6" data-testid="import-detail-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
          <Link to="/app/admin/imports" className="hover:underline">Imports</Link>
          {" / "}
          <span className="capitalize">{job.type.replace(/_/g, " ")}</span>
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">
          Job {jobId.slice(0, 8)}…
        </h1>
        <p className="mt-2 text-muted-foreground">
          {job.original_filename} · {job.total_rows} rows total · {job.valid_rows} valid, {job.error_rows} errors
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <StatTile label="Total rows" value={job.total_rows} testId="stat-total-rows" />
        <StatTile label="Valid" value={job.valid_rows} tone="emerald" testId="stat-valid-rows" />
        <StatTile label="Errors" value={job.error_rows} tone={job.error_rows ? "rose" : "slate"} testId="stat-error-rows" />
        <StatTile
          label={committing ? "Progress" : "Committed"}
          value={
            progress && !isTerminal
              ? `${progress.processed}/${progress.total}`
              : job.commit_processed
          }
          tone="blue"
          testId="stat-committed-rows"
        />
      </div>

      {job.warnings?.length > 0 && (
        <Card>
          <CardContent className="pt-6 text-sm">
            <div className="text-xs font-medium uppercase tracking-widest text-amber-700">Warnings</div>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-amber-800">
              {job.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      {job.sample_valid?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Valid rows preview (first 20)</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <RowTable rows={job.sample_valid} testIdPrefix="valid" />
          </CardContent>
        </Card>
      )}

      {job.sample_errors?.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base text-rose-700">
              Error rows (first 20 of {job.error_rows})
            </CardTitle>
            <Button size="sm" variant="outline" onClick={downloadErrors} data-testid="import-download-errors">
              <Download className="h-3.5 w-3.5" /> Download full errors.csv
            </Button>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <table className="min-w-full text-xs" data-testid="import-errors-table">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="px-3 py-2">Row #</th>
                  <th className="px-3 py-2">Column</th>
                  <th className="px-3 py-2">Message</th>
                </tr>
              </thead>
              <tbody>
                {job.sample_errors.map((e, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2 tabular-nums">{e.row_number}</td>
                    <td className="px-3 py-2 font-mono">{e.column || "—"}</td>
                    <td className="px-3 py-2 text-rose-700">{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {canCommit && (
          <Button onClick={commit} disabled={committing} data-testid="import-commit-btn">
            {committing
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Committing…</>
              : <><CheckCircle2 className="h-4 w-4" /> Commit {job.valid_rows} valid row{job.valid_rows === 1 ? "" : "s"}</>
            }
          </Button>
        )}
        {isTerminal && (
          <div className="flex items-center gap-2 text-sm">
            {job.status === "committed" && (
              <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> All rows committed.</>
            )}
            {job.status === "committed_with_errors" && (
              <><CheckCircle2 className="h-4 w-4 text-amber-600" /> Committed with some errors.</>
            )}
            {(job.status === "failed" || job.status === "failed_timeout") && (
              <><XCircle className="h-4 w-4 text-rose-600" /> Failed. See errors above.</>
            )}
            <Button asChild size="sm" variant="ghost" className="ml-2">
              <Link to="/app/admin/imports">Back to jobs <ArrowRight className="h-3.5 w-3.5" /></Link>
            </Button>
          </div>
        )}
        <div className="ml-auto flex items-center gap-3">
          {Object.entries(job.inserted_ids_by_collection || {}).map(([col, ids]) => (
            <Badge key={col} variant="outline" className="text-emerald-700"
                   data-testid={`inserted-${col}`}>
              +{ids.length} {col.replace(/_/g, " ")}
            </Badge>
          ))}
          {Object.entries(job.updated_ids_by_collection || {}).map(([col, ids]) => (
            <Badge key={col} variant="outline" className="text-blue-700"
                   data-testid={`updated-${col}`}>
              ~{ids.length} {col.replace(/_/g, " ")}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}


function StatTile({ label, value, tone = "slate", testId }) {
  const cls = {
    slate: "text-primary",
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    blue: "text-blue-700",
  }[tone] || "text-primary";
  return (
    <div className="card-elevate rounded-lg border border-border bg-card p-3 shadow-sm"
         data-testid={testId}>
      <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={`mt-2 font-display text-2xl font-semibold ${cls} tabular-nums`}>
        {value}
      </div>
    </div>
  );
}

function RowTable({ rows, testIdPrefix }) {
  if (!rows?.length) return null;
  const columns = Object.keys(rows[0]);
  return (
    <table className="min-w-full text-xs" data-testid={`import-${testIdPrefix}-table`}>
      <thead>
        <tr className="text-left text-muted-foreground">
          {columns.map((c) => (
            <th key={c} className="whitespace-nowrap px-3 py-2 font-medium">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-border">
            {columns.map((c) => (
              <td key={c} className="max-w-[220px] truncate whitespace-nowrap px-3 py-2">
                {String(r[c] ?? "")}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
